import {
  TechLogToRusadaDefectInput,
  TechLogToRusadaDefectCloseInput,
  TechLogToRusadaDefectDeferInput,
} from './validation';

/**
 * Default transformation schema for mapping TechLog defects to Rusada defects
 *
 * Maps TechLog defect data structure to Rusada defect input format.
 * This enables bidirectional defect synchronization between systems.
 */
export const techlogDefectToRusadaSchema = {
  // REQUIRED: Aircraft registration ID (must be a number)
  registrationId: (input: TechLogToRusadaDefectInput) => {
    return input.rusada?.registration?.id;
  },

  // REQUIRED: Defect description
  defectText: (input: TechLogToRusadaDefectInput) => {
    return (
      input.techlog?.defect?.details || input.techlog?.defect?.itemDescription
    );
  },

  // REQUIRED: Defect category ID (number)
  // Per Veryon API deep dive: 2=Defect, 3=Component Defect
  // Query /v1/Defects/Categories at runtime and use IDs from response
  defectCategoryId: (input: TechLogToRusadaDefectInput) => {
    const categories = input.rusada?.defectCategories;

    if (!categories || categories.length === 0) {
      // Fallback to default if categories aren't available
      // Per deep dive: 2=Defect (general defect category)
      return 2;
    }

    // Per Veryon API deep dive findings:
    // - 2 = Defect (general defect, isComponentDefect=false)
    // - 3 = Component Defect (isComponentDefect=true)
    // Use non-component defect category (id: 2) for standard defects
    // Only use component defect (id: 3) if TechLog defect is explicitly a component defect
    const generalCategory = categories.find((cat) => !cat.isComponentDefect);
    if (generalCategory) {
      return generalCategory.id;
    }

    // Fallback to first available category (should be 2=Defect)
    return categories[0]?.id || 2;
  },

  techLogId: (input: TechLogToRusadaDefectInput) =>
    input.rusada?.sector?.techLogId,

  flightId: (input: TechLogToRusadaDefectInput) =>
    input.rusada?.sector?.sectorId,

  // REQUIRED: Sequence number (string) - Runtime required despite schema showing optional
  // Per Veryon API deep dive: sequenceNumber is required at runtime (error if missing)
  // Maps TechLog defect number to Rusada sequenceNumber (becomes defectSequenceNo)
  sequenceNumber: (input: TechLogToRusadaDefectInput) => {
    const defectNumber = input.techlog?.defect?.number;
    if (defectNumber !== undefined && defectNumber !== null) {
      return defectNumber.toString();
    }
    // Generate a sequence number if TechLog defect number is missing
    // Use defect ID as fallback to ensure we always have a sequenceNumber
    return input.techlog?.defect?.id;
  },

  // itemNumber uniqueness is per-aircraft in Tracking+ — no prefix needed.
  itemNumber: (input: TechLogToRusadaDefectInput) => {
    const defectNumber = input.techlog?.defect?.number;
    if (defectNumber === undefined || defectNumber === null) return undefined;
    return String(defectNumber);
  },

  // OPTIONAL: Component ID (number) - Not available in TechLog defect structure
  componentId: (input: TechLogToRusadaDefectInput) => undefined,

  // 1=MAREP, 2=PIREP. TechLog enum is "pilot"|"maintenance"|"cabin".
  // TechLog users are predominantly pilots — default to PIREP unless
  // explicitly reported by maintenance.
  defectTypeId: (input: TechLogToRusadaDefectInput) => {
    const raw = input.techlog?.defect?.reportedByType;
    if (raw?.toLowerCase() === 'maintenance') {
      return 1;
    }
    return 2;
  },

  // OPTIONAL: ATA chapter ID (number)
  // Matches TechLog ataChapter + ataSection against Rusada "ata" field (e.g., "02-00")
  // Falls back to ATA PENDING (ata = "00-00") if no match
  ataChapterId: (input: TechLogToRusadaDefectInput) => {
    const ataChapters = input.rusada?.ataChapters;
    if (!ataChapters || ataChapters.length === 0) {
      return undefined;
    }

    const ataPendingId = ataChapters.find((ch) => ch.ata === '00-00')?.id;

    const ataChapter = input.techlog?.defect?.ataChapter;
    if (ataChapter === undefined || ataChapter === null) {
      return ataPendingId;
    }

    const ataSection = input.techlog?.defect?.ataSection ?? 0;

    // Build "XX-XX" format from TechLog ataChapter and ataSection
    const ataCode = `${String(ataChapter).padStart(2, '0')}-${String(
      ataSection,
    ).padStart(2, '0')}`;

    // Find matching ATA chapter by ata field
    const match = ataChapters.find((ch) => ch.ata === ataCode);

    return match?.id ?? ataPendingId;
  },

  // OPTIONAL: Base location ID (number)
  // NOTE: baseId refers to Rusada "Bases" (GET /v1/Bases), NOT "Places" (GET /v1/Places).
  // Bases are operator-configured maintenance/reporting locations, a small subset of all airports.
  // TechLog does not have an equivalent "bases" concept - only airports.
  // Currently skipping this field as there's no reliable mapping from TechLog airports to Rusada bases.
  // Default value on Rusada is 1 ("CRN").
  // Future: Could fetch /v1/Bases and match by name/code if operator configures base mappings.
  baseId: () => undefined,

  // OPTIONAL: Structural item flag (boolean)
  isStructuralItem: (input: TechLogToRusadaDefectInput) => {
    const ataChapter = input.techlog?.defect?.ataChapter;

    // Structural items typically involve certain ATA chapters
    if (ataChapter) {
      const chapter = Number(ataChapter);
      // ATA chapters that typically involve structural items
      const structuralChapters = [53, 54, 55, 56, 57]; // Fuselage, Nacelles, Stabilizers, Windows, Wings
      return structuralChapters.includes(chapter);
    }

    return false;
  },

  // OPTIONAL: Reliability exclusion flag (boolean)
  excludeFromReliability: (input: TechLogToRusadaDefectInput) => {
    const defectType = input.techlog?.defect?.defectType?.toLowerCase();

    // Exclude certain types from reliability analysis
    return defectType === 'maintenance' || defectType === 'scheduled';
  },

  // OPTIONAL: User ID (number)
  // Maps TechLog reportedById → Rusada employee ID via email lookup
  userId: (input: TechLogToRusadaDefectInput) => {
    const rusadaEmployee = input.rusada?.reportingUser;
    return rusadaEmployee?.id || undefined;
  },
};

/**
 * Close schema for mapping TechLog resolved defects to Rusada defect close input
 */
export const techlogDefectCloseToRusadaSchema = {
  defectId: (input: TechLogToRusadaDefectCloseInput) => input.rusada.defect.id,

  carriedOutDateTimeUtc: (input: TechLogToRusadaDefectCloseInput) => {
    const updatedAt = input.techlog?.defect?.updatedAt;
    return updatedAt ? new Date(updatedAt).toISOString() : undefined;
  },

  techLogSectorId: (input: TechLogToRusadaDefectCloseInput) =>
    input.rusada.defect.flightId ?? input.rusada?.sector?.sectorId,

  // Refine in validation guarantees reportingUser at runtime; optional type is
  // a side-effect of the refine pattern (see validation.ts).
  carriedOutByEmployeeId: (input: TechLogToRusadaDefectCloseInput) =>
    input.rusada.reportingUser?.id,

  actionText: (input: TechLogToRusadaDefectCloseInput) => {
    // Use resolution description (notes field from TechLog) when available
    const resolution = input.techlog?.defectResolution;
    if (resolution?.description) {
      return resolution.description;
    }

    // Fallback for defects without resolution data
    const defect = input.techlog?.defect;

    if (defect?.troubleshootingSteps) {
      return defect.troubleshootingSteps;
    }

    return 'Defect resolved at TrustFlight TechLog';
  },

  timeSpentMinutes: () => undefined,

  faultCode: (input: TechLogToRusadaDefectCloseInput) => {
    const ataChapter = input.techlog.defect.ataChapter;
    const ataSection = input.techlog.defect.ataSection;
    if (ataChapter && ataSection) {
      return `ATA ${ataChapter}-${ataSection}`;
    }
    return undefined;
  },
};

/**
 * Defer schema for mapping TechLog deferred defects to Rusada defect defer input
 */
export const techlogDefectDeferToRusadaSchema = {
  defectId: (input: TechLogToRusadaDefectDeferInput) => input.rusada.defect.id,

  // REQUIRED: Deferral reason/notes
  actionText: (input: TechLogToRusadaDefectDeferInput) => {
    const defect = input.techlog?.defect;
    return defect?.reasonToDefer || 'Deferred at TrustFlight TechLog';
  },

  addReference: (input: TechLogToRusadaDefectDeferInput) => {
    const defect = input.techlog?.defect;
    const defectNum = defect?.number?.toString() ?? 'X';
    const userRef = defect?.deferApprovalReference?.substring(0, 12) || 'N/A';
    return `${defectNum}-${userRef}`.substring(0, 20);
  },

  // REQUIRED: TechLog sector ID (the sector within the TechLog, not the TechLog itself).
  // Input validation refine guarantees rusada.sector at runtime; the optional
  // chain only satisfies the inferred type, which keeps `sector` optional.
  techLogSectorId: (input: TechLogToRusadaDefectDeferInput) =>
    input.rusada.defect.flightId ?? input.rusada.sector?.sectorId,

  // 1=MEL_A..4=MEL_D, 5=NO_MEL, 6=HUSB. iPad emits variants like "MEL_C",
  // "MEL C", "NON_DEFERRED" — normalise the same way as defectTypeId so
  // format drift doesn't silently fall through to the MEL C default.
  melCategoryId: (input: TechLogToRusadaDefectDeferInput) => {
    const raw = input.techlog?.defect?.rectificationCategory;
    const normalized = (raw ?? '').toLowerCase().replace(/[_\s-]/g, '');
    const map: Record<string, number> = {
      a: 1,
      mela: 1,
      b: 2,
      melb: 2,
      c: 3,
      melc: 3,
      d: 4,
      meld: 4,
      nondeferred: 5,
      nomel: 5,
      advisoryonly: 6,
      husb: 6,
    };
    return map[normalized] ?? 3;
  },

  // REQUIRED: Target date/time in ISO format
  // Falls back to calculating based on MEL category days if TechLog has no due date
  // Clamped to at least Rusada openDate — Rusada rejects target dates before the defect's created date
  targetDateUtc: (input: TechLogToRusadaDefectDeferInput) => {
    const defect = input.techlog?.defect;
    let target: Date;

    if (defect?.dueDateTime) {
      target = new Date(defect.dueDateTime);
    } else if (defect?.dueDate) {
      target = new Date(`${defect.dueDate}T23:59:59Z`);
    } else {
      // Fallback: calculate target date based on MEL category days
      // MEL A=1d, B=3d, C=10d, D=120d, NO_MEL=1d, HUSB=365d
      const category = defect?.rectificationCategory?.toUpperCase() ?? '';
      const melDays: Record<string, number> = {
        A: 1,
        B: 3,
        C: 10,
        D: 120,
        NONDEFERRED: 1,
        ADVISORYONLY: 365,
      };
      const days = melDays[category] ?? 10;
      const baseDate = defect?.deferredAt
        ? new Date(defect.deferredAt)
        : new Date();
      target = new Date(baseDate.getTime() + days * 24 * 60 * 60 * 1000);
    }

    const openDate = new Date(input.rusada.defect.openDate!);
    if (openDate > target) {
      target = openDate;
    }

    return target.toISOString();
  },

  // REQUIRED: MEL reference (can be empty string)
  melReference: (input: TechLogToRusadaDefectDeferInput) => {
    return input.techlog?.defect?.melItemId || '';
  },

  // OPTIONAL: Labor time in minutes
  timeSpentMinutes: () => undefined,

  // OPTIONAL: Deferral reason lookup ID
  deferralReasonId: () => undefined,

  // OPTIONAL: Life-based limit in minutes
  targetLifeMinutes: (input: TechLogToRusadaDefectDeferInput) => {
    const limitSeconds = input.techlog?.defect?.limitFlightSeconds;
    if (limitSeconds && limitSeconds > 0) {
      return Math.floor(limitSeconds / 60);
    }
    return undefined;
  },

  // OPTIONAL: Cycle-based limit
  targetLifeCycles: (input: TechLogToRusadaDefectDeferInput) => {
    const limitCycles = input.techlog?.defect?.limitCycles;
    if (limitCycles && limitCycles > 0) {
      return limitCycles;
    }
    return undefined;
  },

  // Date/time when deferral was carried out
  // Use later of TechLog deferredAt or Rusada openDate to satisfy API validation
  carriedOutDateTimeUtc: (input: TechLogToRusadaDefectDeferInput) => {
    const deferredAt = new Date(input.techlog.defect.deferredAt!);
    const openDate = new Date(input.rusada.defect.openDate!);
    return (openDate > deferredAt ? openDate : deferredAt).toISOString();
  },

  // OPTIONAL: Employee who carried out the deferral
  // Maps TechLog deferredById → Rusada employee ID via email lookup
  carriedOutByEmployeeId: (input: TechLogToRusadaDefectDeferInput) =>
    input.rusada?.deferringUser?.id,
};

export default techlogDefectToRusadaSchema;
